/**
 * Очередь визуальных FX: приоритет, пауза между задачами, реф-счётчик критических блокировок (release).
 *
 * @module components/online/hooks/useBelotVisualFxQueue
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Приоритеты очереди: меньшее число — раньше в сортировке.
 * Блокировка `acquireCritical` (раздача, шулер) → 10 торги → 20 ревил комбо → 30 бела →
 * 31 эмодзи над местом → 35 оверлей конца взятки → 40 combo scoring → 50 шулер (метка).
 */
export const BELOT_FX_PRIORITY = {
  DEAL_BLOCK: 5,
  AUCTION_ACTION: 10,
  /** Превью «Объявить» у героя (хардкор) до публичного ревила после коммита всех четырёх. */
  HARDCORE_SELF_PREVIEW: 15,
  PRE_PLAY_COMBO_REVEAL: 20,
  BELA_SPEECH: 30,
  /** Реакция-эмодзи бота над местом — после речи Блот/Реблот, без наложения. */
  BOT_SEAT_EMOTE: 31,
  TRICK_END_OVERLAY: 35,
  COMBO_REVEAL: 40,
  SHULER_CRITICAL: 50,
};

const DEFAULT_GAP_MS = 300;

function sleep(ms) {
  return new Promise((r) => {
    setTimeout(r, Math.max(0, ms));
  });
}

/**
 * @typedef {{
 *   id: string,
 *   priority: number,
 *   run: () => void | Promise<void>,
 *   minGapMs?: number,
 * }} BelotFxQueueItem
 */

/**
 * @param {{ defaultMinGapMs?: number }} [opts]
 */
export function useBelotVisualFxQueue(opts = {}) {
  const defaultGapMs =
    typeof opts.defaultMinGapMs === 'number' && opts.defaultMinGapMs >= 0
      ? opts.defaultMinGapMs
      : DEFAULT_GAP_MS;

  /** @type {React.MutableRefObject<BelotFxQueueItem[]>} */
  const itemsRef = useRef([]);
  const seqRef = useRef(0);
  /** Отрицательные seq для `prepend` — обрабатываются раньше при том же priority. */
  const prependSeqRef = useRef(0);
  const processingRef = useRef(false);
  /** Сколько активных критических блокировок (раздача WAAPI, шулер и т.д.). Пока > 0 — очередь не берёт задачи. */
  const activeCriticalLocksRef = useRef(0);
  const mountedRef = useRef(true);

  const processLoop = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (mountedRef.current && itemsRef.current.length) {
        if (activeCriticalLocksRef.current > 0) {
          await sleep(32);
          if (!mountedRef.current) return;
          continue;
        }

        itemsRef.current.sort((a, b) => {
          if (a.priority !== b.priority) return a.priority - b.priority;
          return (a._seq || 0) - (b._seq || 0);
        });
        const item = itemsRef.current.shift();
        if (!item) break;

        try {
          await Promise.resolve(item.run());
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('[useBelotVisualFxQueue]', e);
          }
        }
        if (!mountedRef.current) return;
        const gap = typeof item.minGapMs === 'number' ? item.minGapMs : defaultGapMs;
        if (itemsRef.current.length > 0 && gap > 0) {
          await sleep(gap);
        }
      }
    } finally {
      processingRef.current = false;
    }
  }, [defaultGapMs]);

  const processLoopRef = useRef(processLoop);
  processLoopRef.current = processLoop;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      itemsRef.current = [];
      activeCriticalLocksRef.current = 0;
    };
  }, []);

  /**
   * Блокирует drain очереди до вызова возвращённой `release` (идемпотентно, один раз).
   * @returns {() => void}
   */
  const acquireCritical = useCallback(() => {
    activeCriticalLocksRef.current += 1;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      activeCriticalLocksRef.current = Math.max(0, activeCriticalLocksRef.current - 1);
      queueMicrotask(() => {
        void processLoopRef.current();
      });
    };
  }, []);

  const clear = useCallback(() => {
    itemsRef.current = [];
  }, []);

  const isBlockingBusy = useCallback(() => {
    return (
      itemsRef.current.length > 0 ||
      processingRef.current ||
      activeCriticalLocksRef.current > 0
    );
  }, []);

  const waitUntilIdle = useCallback(async () => {
    while (mountedRef.current) {
      if (!isBlockingBusy()) return;
      await sleep(32);
    }
  }, [isBlockingBusy]);

  const pushItem = useCallback(
    (item, /** @type {number} */ seq) => {
      if (!mountedRef.current) return;
      const id = String(item?.id || '').trim() || `fx-${Date.now()}`;
      const priority = Number(item.priority) || 0;
      const run = typeof item.run === 'function' ? item.run : () => {};
      itemsRef.current = itemsRef.current.filter((x) => x.id !== id);
      itemsRef.current.push({
        id,
        priority,
        run,
        minGapMs: item.minGapMs,
        _seq: seq,
      });
      queueMicrotask(() => {
        void processLoopRef.current();
      });
    },
    [],
  );

  const enqueue = useCallback(
    (/** @type {BelotFxQueueItem} */ item) => {
      seqRef.current += 1;
      pushItem(item, seqRef.current);
    },
    [pushItem],
  );

  const prepend = useCallback(
    (/** @type {BelotFxQueueItem} */ item) => {
      prependSeqRef.current -= 1;
      pushItem(item, prependSeqRef.current);
    },
    [pushItem],
  );

  return useMemo(
    () => ({ enqueue, prepend, clear, acquireCritical, isBlockingBusy, waitUntilIdle }),
    [enqueue, prepend, clear, acquireCritical, isBlockingBusy, waitUntilIdle],
  );
}
